import HighchartsV12 from "highcharts-v12"
import HrmChartController, { NEUTRAL_TAG, TAG_CLASSES } from "./hrm_chart_controller"
import { highchartsSettingsV12 } from "@xlayer/composables/chartModules/highchartsSetup"
import { withDateAxis } from "@xlayer/composables/chartModules/dateAxis"

const DASHBOARD_ROW_HEIGHT = 20
const MAX_ENLARGEMENT = 1
const CHART_GAP = 40
const CELL_HEADER = 90
const PACKING_SCALE = 0.8

export default class extends HrmChartController {
  static values = { ...HrmChartController.values, chapters: String, footer: String }

  flowBody: HTMLElement | null = null

  async connect() {
    highchartsSettingsV12()
    HighchartsV12.setOptions({ lang: { thousandsSep: "." } })

    const data = JSON.parse(this.dataValue) || []
    const layout = this.sortGridByPosition(JSON.parse(this.layoutValue))
    const chapters = JSON.parse(this.chaptersValue || "[]")
    const renders = []

    if (this.idValue !== "") {
      this.renderSingleComponent(data.find(component => component.id === this.idValue), renders)
    } else {
      const renderedChapters = new Set()
      this.flowBody = null

      layout.forEach(item => {
        const component = data.find(component => component.id === item.componentId)
        if (!component) return

        const chapter = this.chapterOf(item, layout, chapters)

        if (component.type === "chart" && component.highchartsData) {
          if (!chapter) {
            this.renderSingleComponent(component, renders)
            return
          }
          if (renderedChapters.has(chapter)) return

          renderedChapters.add(chapter)
          this.flowBody = null
          const charts = layout
            .filter(other => this.chapterOf(other, layout, chapters) === chapter)
            .map(other => ({ item: other, component: data.find(candidate => candidate.id === other.componentId) }))
            .filter(({ component }) => component?.type === "chart" && component.highchartsData)
          renders.push(...this.renderChapterPage(chapter, charts))
        } else if (component.type === "tags") {
          this.flow(this.tagsBlock(component, chapter))
        } else if (component.type === "table") {
          this.flowBody = null
          this.handleTableComponent(component)
        }
      })
    }

    await Promise.allSettled(renders)
    this.waitForRenderingComplete(this.element)
  }

  chapterOf(item, layout, chapters) {
    const heading = layout
      .filter(other => other.y < item.y && other.x < item.x + item.w && item.x < other.x + other.w)
      .filter(other => chapters.some(chapter => chapter.id === other.componentId))
      .sort((a, b) => b.y - a.y)[0]
    if (heading) return chapters.find(chapter => chapter.id === heading.componentId)

    return chapters.find(chapter => chapter.componentIds.includes(item.componentId))
  }

  renderSingleComponent(component, renders) {
    if (!component) return

    switch (component.type) {
      case "chart": {
        const chartId = `chart-container-${component.id}`
        this.element.appendChild(this.createComponentTemplate("hrm-chart-template", chartId, component.display))
        renders.push(this.renderChart(JSON.parse(JSON.stringify(component.highchartsData)), chartId))
        break
      }
      case "table":
        this.handleTableComponent(component)
        break
      case "tags":
        this.handleTagsComponent(component)
        break
      case "vacancies":
        this.handleVacanciesComponent(component)
        break
    }
  }

  flow(block: HTMLElement) {
    if (!this.flowBody) this.flowBody = this.newFlowPage()
    this.flowBody.appendChild(block)

    if (this.flowBody.scrollHeight > this.flowBody.clientHeight && this.flowBody.children.length > 1) {
      block.remove()
      this.flowBody = this.newFlowPage()
      this.flowBody.appendChild(block)
    }
  }

  newFlowPage(): HTMLElement {
    const page = (document.getElementById("reko-flow-template") as HTMLTemplateElement).content.cloneNode(true) as DocumentFragment
    page.querySelector(".footer").textContent = this.footerValue
    const body = page.querySelector(".flow") as HTMLElement
    this.element.appendChild(page)
    return body
  }

  blockHeading(component, chapter): HTMLElement {
    const section = document.createElement("section")
    const title = document.createElement("h2")
    title.className = "text-4xl font-bold"
    title.textContent = this.plainText(component.display?.header?.title) || chapter?.title || ""
    section.appendChild(title)

    const text = component.description || chapter?.lead
    if (text) {
      const description = document.createElement("p")
      description.className = "text-xl text-gray-600 mt-2"
      description.textContent = text
      section.appendChild(description)
    }
    return section
  }

  tagsBlock(component, chapter): HTMLElement {
    const section = this.blockHeading(component, chapter)
    const list = document.createElement("ul")
    list.className = "flex flex-wrap gap-3 mt-5"
    ;(component.tags || []).forEach(tag => {
      const item = document.createElement("li")
      item.className = `rounded-full border px-4 py-2 text-lg ${TAG_CLASSES[component.color] ?? NEUTRAL_TAG}`
      item.textContent = tag
      list.appendChild(item)
    })
    section.appendChild(list)
    return section
  }

  plainText(html) {
    const element = document.createElement("div")
    element.innerHTML = html || ""
    return element.textContent.trim()
  }

  renderChapterPage(chapter, charts) {
    const rows = []
    charts.forEach(chart => {
      const row = rows.find(row => row[0].item.y === chart.item.y)
      row ? row.push(chart) : rows.push([chart])
    })

    const renders = []
    let page = this.newChapterPage(chapter, true)
    let pageRows = []
    let used = 0

    const fill = () => {
      page.appendChild(this.spacer(1))
      const charts = pageRows.flatMap(row => this.renderRow(page, row))
      page.appendChild(this.spacer(2))
      charts.forEach(({ chartId, component }) => {
        renders.push(this.renderChart(JSON.parse(JSON.stringify(component.highchartsData)), chartId))
      })
    }

    rows.forEach(row => {
      const needed = this.rowSpan(row) * DASHBOARD_ROW_HEIGHT * PACKING_SCALE + CELL_HEADER + CHART_GAP
      if (pageRows.length > 0 && used + needed > page.clientHeight) {
        fill()
        page = this.newChapterPage(chapter, false)
        pageRows = []
        used = 0
      }
      pageRows.push(row)
      used += needed
    })
    fill()

    return renders
  }

  spacer(grow: number): HTMLElement {
    const spacer = document.createElement("div")
    spacer.style.flex = `${grow} 1 0`
    return spacer
  }

  rowSpan(row) {
    return Math.max(...row.map(({ item }) => item.h))
  }

  newChapterPage(chapter, first: boolean): HTMLElement {
    const page = (document.getElementById("reko-chapter-template") as HTMLTemplateElement).content.cloneNode(true) as DocumentFragment
    page.querySelector(".title").textContent = first ? chapter.title : `${chapter.title} (Fortsetzung)`
    page.querySelector(".footer").textContent = this.footerValue

    if (first) {
      page.querySelector(".lead").textContent = chapter.lead || ""
      const definitions = page.querySelector(".definitions")
      chapter.definitions.forEach(definition => {
        const item = document.createElement("li")
        const label = document.createElement("b")
        label.className = "text-gray-900"
        label.textContent = `${definition.label}: `
        item.append(label, definition.text)
        definitions.appendChild(item)
      })
    }

    const container = page.querySelector(".charts") as HTMLElement
    this.element.appendChild(page)
    return container
  }

  renderRow(page: HTMLElement, row) {
    const span = this.rowSpan(row)
    const rowElement = document.createElement("div")
    rowElement.className = "flex gap-12 w-full min-h-0"
    rowElement.style.flex = `${span * 100} 1 0`
    const hasHeader = row.some(({ component }) => component.display?.header?.title || component.display?.header?.tooltip?.value)
    rowElement.style.maxHeight = `${span * DASHBOARD_ROW_HEIGHT * MAX_ENLARGEMENT + (hasHeader ? CELL_HEADER : 0)}px`
    page.appendChild(rowElement)

    const totalWidth = row.reduce((sum, { item }) => sum + item.w, 0)
    return row.map(({ item, component }) => {
      const cell = document.createElement("div")
      cell.className = "flex flex-col min-w-0 min-h-0"
      cell.style.flex = `${item.w / totalWidth} 1 0`

      const title = this.plainText(component.display?.header?.title)
      if (title) {
        const heading = document.createElement("h3")
        heading.className = "text-2xl font-semibold"
        heading.textContent = title
        cell.appendChild(heading)
      }
      const question = this.plainText(component.display?.header?.tooltip?.value)
      if (question && question !== title) {
        const text = document.createElement("p")
        text.className = "text-lg text-gray-600 mt-1"
        text.textContent = question
        cell.appendChild(text)
      }

      const chartId = `chart-container-${component.id}`
      const chart = document.createElement("div")
      chart.id = chartId
      chart.className = `w-full grow min-h-0 ${title || question ? "mt-4" : ""}`
      cell.appendChild(chart)
      rowElement.appendChild(cell)

      return { chartId, component }
    })
  }

  async renderChart(rawChartData, chartId, constructorType = "chart") {
    const chartData = withDateAxis(rawChartData)
    chartData.chart = chartData.chart || {}
    chartData.chart.spacingLeft = Math.max(chartData.chart.spacingLeft ?? 0, 20)
    const heatmap = chartData.chart.type === "heatmap"
    ;[chartData.xAxis].flat().filter(Boolean).forEach(axis => {
      axis.labels = { ...axis.labels, style: { ...axis.labels?.style } }
    })
    const plainLabels = labels => [labels].flat().filter(Boolean).forEach(label => {
      label.style = { ...label.style, textOutline: "none" }
    })
    chartData.plotOptions = chartData.plotOptions || {}
    chartData.plotOptions.series = { ...chartData.plotOptions.series, dataLabels: chartData.plotOptions.series?.dataLabels || {} }
    Object.values(chartData.plotOptions).forEach(options => plainLabels(options?.dataLabels))
    ;[chartData.series].flat().filter(Boolean).forEach(series => plainLabels(series.dataLabels))
    const endLabels = ["line", "arearange", "area"].includes(chartData.chart.type)
    chartData.chart.spacingRight = Math.max(chartData.chart.spacingRight ?? 0, endLabels ? 150 : 40)
    if (heatmap) {
      ;[chartData.yAxis].flat().filter(Boolean).forEach(axis => {
        axis.labels = { ...axis.labels, overflow: "allow" }
      })
    }
    await super.renderChart(chartData, chartId, constructorType)

    const container = document.getElementById(chartId)
    const chart = HighchartsV12.charts.find(chart => chart?.renderTo === container)
    if (!chart) return

    if (heatmap) {
      chart.yAxis.forEach(axis => axis.update({ labels: { style: { width: "185px", fontSize: "14px" } } }, false))
      chart.redraw(false)
    } else if (chart.inverted && chart.options.chart.type === "bar") {
      const labelSpace = Math.round(chart.chartWidth * 0.3)
      const fixedMargin = chart.options.chart.marginLeft
      if (fixedMargin) chart.update({ chart: { marginLeft: Math.max(fixedMargin, labelSpace) } }, false)
      const width = `${(fixedMargin ? Math.max(fixedMargin, labelSpace) : labelSpace) - 20}px`
      chart.xAxis.forEach(axis => axis.update({ labels: { style: { width, textOverflow: "ellipsis" } } }, false))
      chart.redraw(false)
    }
  }
}
